import { getPostHog, isPostHogEnabled } from "./posthogClient";

/**
 * The client half of the PostHog <-> Tempo join, for the storefront.
 *
 * Same contract as sh0p-admin's src/lib/trace.ts — a W3C `traceparent` out, the
 * backend's `x-trace-id` back — with one difference that matters a great deal
 * here and not at all there.
 *
 * ── Why the storefront cannot trace every request ────────────────────────────
 *
 * A cross-origin request carrying only CORS-safelisted headers is a "simple
 * request": the browser sends it straight out. Add ANY custom header —
 * `traceparent` included — and it stops being simple, so the browser must first
 * send an OPTIONS preflight and wait for the answer before the real request
 * leaves. That is an extra round trip, on the buyer's mobile connection, before
 * a product image or a price can even be asked for.
 *
 * The admin portal never pays this: every one of its requests already carries
 * `Authorization`, so every one is already preflighted. The storefront is the
 * opposite — most of it is anonymous GETs that are simple today.
 *
 * So the rule here is: attach trace headers only to requests that the browser
 * was going to preflight anyway. In practice that is every mutation — the JSON
 * POSTs that create an order, add to cart, start a payment — which is also
 * exactly the set of requests worth tracing. Anonymous catalogue GETs stay
 * simple and stay fast, and their correlation comes from the response instead:
 * `x-trace-id` is readable without making the request non-simple (an exposed
 * response header costs nothing on the way out).
 *
 * Net effect on the buyer: zero added round trips, on any request.
 */

const HEX = "0123456789abcdef";

/** CORS-safelisted request headers: these never, on their own, force a preflight. */
const SIMPLE_HEADERS = new Set(["accept", "accept-language", "content-language", "content-type"]);

/**
 * The only three Content-Type values a simple request may carry. `application/
 * json` is deliberately absent from this list in the spec, which is why every
 * JSON POST in this app is already preflighted.
 */
const SIMPLE_CONTENT_TYPES = [
  "application/x-www-form-urlencoded",
  "multipart/form-data",
  "text/plain",
];

const SIMPLE_METHODS = new Set(["GET", "HEAD", "POST"]);

const randomHex = (bytes) => {
  const out = new Array(bytes * 2);
  const buf = new Uint8Array(bytes);

  try {
    crypto.getRandomValues(buf);
  } catch {
    // Only reached in an exotic webview with no WebCrypto. Uniqueness is all
    // that is asked of a trace id; nothing is authorised by one.
    for (let i = 0; i < bytes; i += 1) buf[i] = Math.floor(Math.random() * 256);
  }

  for (let i = 0; i < bytes; i += 1) {
    out[i * 2] = HEX[buf[i] >> 4];
    out[i * 2 + 1] = HEX[buf[i] & 0x0f];
  }
  return out.join("");
};

/** Read a header value out of any of the three shapes fetch() accepts. */
const headerEntries = (headers) => {
  if (!headers) return [];
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return Array.from(headers.entries());
  }
  if (Array.isArray(headers)) return headers;
  return Object.entries(headers);
};

/**
 * Whether this request is already non-simple, i.e. the browser is going to
 * preflight it with or without us, so trace headers are free.
 *
 * Conservative on purpose: anything this cannot positively identify as already
 * preflighted is treated as simple and left alone. A missed trace costs a
 * correlation; a wrong answer costs every buyer an extra round trip.
 */
export const isAlreadyPreflighted = (method, headers) => {
  if (!SIMPLE_METHODS.has(String(method || "GET").toUpperCase())) return true;

  return headerEntries(headers).some(([rawName, rawValue]) => {
    const name = String(rawName).toLowerCase();
    if (!SIMPLE_HEADERS.has(name)) return true;
    if (name !== "content-type") return false;

    const value = String(rawValue).split(";")[0].trim().toLowerCase();
    return !SIMPLE_CONTENT_TYPES.includes(value);
  });
};

/**
 * Mint a trace context for one outgoing request.
 *
 * The sampled flag is set (`-01`) because the backend samples in Alloy after
 * the whole trace is known, not at the head — see backend config/tracing.js.
 *
 * Costs a 16-byte random draw and a hex encode: single-digit microseconds,
 * synchronous, no network. PostHog is read opportunistically and never waited
 * on; if the SDK has not loaded, the two posthog headers are simply absent.
 */
export const createTraceContext = () => {
  const traceId = randomHex(16);
  const headers = {
    traceparent: `00-${traceId}-${randomHex(8)}-01`,
  };

  try {
    if (isPostHogEnabled()) {
      const sessionId = getPostHog()?.get_session_id?.();
      if (sessionId) headers["x-posthog-session-id"] = sessionId;
    }
  } catch {
    // Analytics must never be able to break a checkout.
  }

  // Deliberately NOT sent from the storefront: `x-posthog-distinct-id`.
  //
  // This app identifies a buyer by their phone number (utils/analytics.js, at
  // order confirmation), so the distinct id IS a phone number. A span attribute
  // is no better a place for one than a Mongo filter or a query string, both of
  // which the backend's tracing already refuses to record. The session id is
  // the join that matters, and PostHog resolves the person from it anyway.
  //
  // Admin does send it — there a distinct id is a storeId. See src/lib/trace.ts.

  return { traceId, headers };
};

/**
 * The most recent trace id this page has seen, outbound or inbound.
 *
 * Why this exists: a `$exception` is not a request. A React render crash, a
 * `window.onerror` from a third-party script, an unhandled rejection — none of
 * them carry a trace context of their own, so there is nothing to read one off.
 * Without this they reach PostHog with no route back to Tempo at all, which is
 * exactly the dead end a session-id-only lookup hits when tail sampling has
 * dropped the session's ordinary 200s.
 *
 * So the correlation offered is "the last request this page made before the
 * error", which is an approximation and is deliberately named as one on the
 * event (`trace_id_is_approximate`). For the overwhelmingly common shape — a
 * fetch resolves, state updates, the render throws — it is the right trace. For
 * an error with no preceding request it is simply absent.
 *
 * Module-scoped rather than stored: it must not outlive the page, or an error
 * would be correlated to a trace from a previous route.
 */
let lastTraceId;

/** Record a trace id as the page's current correlation point. */
export const setLastTraceId = (traceId) => {
  if (typeof traceId === "string" && traceId.length > 0) lastTraceId = traceId;
};

/** The last trace id seen on this page, or undefined if no request has carried one. */
export const getLastTraceId = () => lastTraceId;

/**
 * Drop the current correlation point. Call on route change: the pages router
 * swaps components without a document load, so this module survives
 * navigation and, without this, an error on the new page would be
 * mis-correlated to a trace from the page the buyer left.
 */
export const clearLastTraceId = () => {
  lastTraceId = undefined;
};

/**
 * The trace id the backend reports for a response.
 *
 * Readable cross-origin only because the API names `x-trace-id` in its CORS
 * `exposedHeaders` (backend src/app.js). This is the correlation path for the
 * simple GETs that deliberately carry no traceparent.
 */
export const readTraceId = (response) => {
  try {
    return response?.headers?.get?.("x-trace-id") || undefined;
  } catch {
    return undefined;
  }
};
